import {parseJsonResponse} from '@ark/react';
import {useSetUserTrackingEmail} from '../hooks/useSetUserTrackingEmail';
import React from 'react';

interface UserContext {
  authToken: null | string;
  setAuthToken: (token: string | null) => void;
  isLoggedIn: boolean;
  sessionExpired: boolean;
  signOut: () => void;
}

export const AUTH_STORAGE_KEY = '__A__';

export const userContext = React.createContext<UserContext>({
  authToken: null,
  setAuthToken: () => {},
  isLoggedIn: false,
  sessionExpired: false,
  signOut: () => {},
});

type Props = {
  children: React.ReactNode;
};
export const UserProvider = ({children}: Props) => {
  const {setUserTrackingEmail} = useSetUserTrackingEmail();
  const currentAuth = localStorage.getItem(AUTH_STORAGE_KEY);
  const [authToken, setAuthToken] = React.useState(currentAuth);
  const [sessionExpired, setSessionExpired] = React.useState(false);
  const authTokenRef = React.useRef(authToken);
  authTokenRef.current = authToken;

  const setAuthTokenFunc = React.useCallback(
    (token: string | null) => {
      if (token) {
        setSessionExpired(false);
        localStorage.setItem(AUTH_STORAGE_KEY, token);
      } else {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
      authTokenRef.current = token;
      setAuthToken(token);
    },
    [setAuthToken],
  );

  const isLoggedIn = React.useMemo(() => !!authToken, [authToken]);

  const signOut = React.useCallback(() => {
    setAuthTokenFunc(null);
  }, [setAuthTokenFunc]);

  const fetchUserDetails = React.useCallback(async () => {
    if (!authToken || authToken === 'null') {
      setAuthTokenFunc(null);
      return;
    }
    try {
      const resp = await fetch(
        `${import.meta.env.VITE_API_ROOT}/auth/users/me/`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${authToken}`,
          },
          mode: 'cors',
          credentials: 'same-origin',
        },
      );
      if (resp.status === 401 && authTokenRef.current === authToken) {
        setSessionExpired(true);
        setAuthTokenFunc(null);
      }
      if (resp.status !== 200) {
        throw Error('Invalid request');
      } else {
        const data = await parseJsonResponse<{email: string}>(resp);
        setUserTrackingEmail(data.email);
      }
    } catch (e) {
      // do nothing
    }
  }, [authToken, setAuthTokenFunc, setUserTrackingEmail]);

  React.useEffect(() => {
    fetchUserDetails();
  }, [fetchUserDetails]);

  return (
    <userContext.Provider
      value={{
        authToken,
        isLoggedIn,
        sessionExpired,
        setAuthToken: setAuthTokenFunc,
        signOut: signOut,
      }}
    >
      {children}
    </userContext.Provider>
  );
};
